/**
 * Build notifications — pura, testeable.
 *
 * Toma snapshots de los stores vivos (enrollments, chat, agents + update states)
 * y devuelve el array de `AppNotification` que el panel debe mostrar.
 *
 * Reglas:
 *  - enrollment: uno por cada enrollment pendiente.
 *  - chat: uno por cada conversación activa con unreadCount > 0.
 *  - update: agrupado cuando hay >= 2 agentes desactualizados; individual si es 1.
 *  - update-failed: uno por agente con updateState.status === 'error'.
 *
 * Si `latestVersion` no está disponible, no se emiten updates pendientes.
 */

import type { Enrollment } from '@/types/enrollment';
import type { ChatConversation } from '@/types/chat';
import type { Agent, AgentUpdateState } from '@/types/agent';
import type { AppNotification, NotificationKind } from '@/store/notificationsStore';

/**
 * Comparador de versiones semver-like (major.minor.patch[-prerelease]).
 * Devuelve -1 si a < b, 0 si iguales, 1 si a > b.
 * Tokens faltantes se tratan como 0. No valida formatos exóticos.
 */
export function compareVersions(a: string, b: string): number {
  const pa = (a || '0').split('.').map((s) => Number.parseInt(s, 10) || 0);
  const pb = (b || '0').split('.').map((s) => Number.parseInt(s, 10) || 0);
  const max = Math.max(pa.length, pb.length);
  for (let i = 0; i < max; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
}

export interface BuildNotificationsInput {
  pendingEnrollments: Enrollment[];
  conversations: ChatConversation[];
  agents: Agent[];
  updateStates: Record<string, AgentUpdateState>;
  latestVersion?: string;
  /** Override para tests. */
  now?: () => Date;
}

/** Trunca un string a N caracteres, agregando elipsis si fue recortado. */
function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length <= n ? s : `${s.slice(0, n).trimEnd()}…`;
}

/** Resuelve hostname legible de un Enrollment (puede tener device anidado). */
function enrollmentHostname(e: Enrollment): string {
  return e.device?.hostname || e.pairingCode || e.enrollmentId;
}

/** OS/arch legible para el body. */
function enrollmentOsArch(e: Enrollment): string {
  const os = e.device?.os;
  const arch = e.device?.arch;
  if (os && arch) return `${os}/${arch}`;
  return os || arch || '';
}

export function buildNotifications(input: BuildNotificationsInput): AppNotification[] {
  const { pendingEnrollments, conversations, agents, updateStates, latestVersion } = input;
  const now = (input.now ?? (() => new Date()))().toISOString();
  const out: AppNotification[] = [];

  // 1. Enrollments pendientes — uno por cada uno.
  for (const e of pendingEnrollments) {
    const host = enrollmentHostname(e);
    const osArch = enrollmentOsArch(e);
    const body = osArch ? `${host} — ${osArch}` : host;
    out.push({
      id: `enrollment:${e.enrollmentId}`,
      kind: 'enrollment',
      title: 'Nuevo enrollment pendiente',
      body,
      createdAt: e.createdAt || now,
      read: false,
      actionPath: '/enrollments',
      meta: { enrollmentId: e.enrollmentId },
    });
  }

  // 2. Chats activos con unread — uno por conversación.
  for (const c of conversations) {
    if (c.status !== 'active') continue;
    if (!c.unreadCount || c.unreadCount <= 0) continue;
    const lastContent = c.lastMessage?.content ?? 'Nuevo mensaje';
    const titleHost = c.agentHostname || c.agentId;
    const lastAt = c.lastMessage?.createdAt || c.lastMessageAt || c.createdAt || now;
    out.push({
      id: `chat:${c.conversationId}`,
      kind: 'chat',
      title: `Mensaje nuevo de ${titleHost}`,
      body: truncate(lastContent, 80),
      createdAt: lastAt,
      read: false,
      actionPath: `/chat/${c.agentId}`,
      meta: { conversationId: c.conversationId, agentId: c.agentId, unreadCount: c.unreadCount },
    });
  }

  // 3. Updates pendientes (outdated) — agrupado si hay >= 2.
  if (latestVersion) {
    const outdated = agents.filter(
      (a) => a.connected && a.version && compareVersions(a.version, latestVersion) < 0
    );
    if (outdated.length === 1) {
      const a = outdated[0]!;
      out.push({
        id: 'update:outdated',
        kind: 'update',
        title: `${a.hostname || a.agentId} desactualizado`,
        body: `v${a.version} → v${latestVersion}`,
        createdAt: now,
        read: false,
        actionPath: '/updates',
        meta: { agentId: a.agentId, kind: 'single' },
      });
    } else if (outdated.length >= 2) {
      const top = outdated
        .slice(0, 3)
        .map((a) => a.hostname || a.agentId)
        .join(', ');
      const rest = outdated.length - 3;
      const body = rest > 0 ? `${top} (+${rest} más)` : top;
      out.push({
        id: 'update:outdated',
        kind: 'update',
        title: `Hay ${outdated.length} agentes desactualizados`,
        body,
        createdAt: now,
        read: false,
        actionPath: '/updates',
        meta: { agentIds: outdated.map((a) => a.agentId), kind: 'grouped' },
      });
    }
  }

  // 4. Updates fallidos — uno por agente con status === 'error'.
  for (const [agentId, us] of Object.entries(updateStates)) {
    if (!us || us.status !== 'error') continue;
    const agent = agents.find((a) => a.agentId === agentId);
    const host = agent?.hostname || agentId;
    out.push({
      id: `update:error:${agentId}`,
      kind: 'update-failed',
      title: `Update falló en ${host}`,
      body: us.error || 'Error desconocido',
      createdAt: us.updatedAt || us.startedAt || now,
      read: false,
      actionPath: '/updates',
      meta: { agentId, retryCount: us.retryCount },
    });
  }

  return out;
}

/** Re-export del kind para convenience en consumers. */
export type { NotificationKind };
